import { breadcrumbSchema, ORG_ID, WEBSITE_ID } from "@/lib/schema";
import { createFileRoute, Link } from "@tanstack/react-router";
import { getBlogIndex } from "@/lib/blog.functions";
import { categoryLabel, formatDateEs, isoDate, type BlogPostCard } from "@/lib/blog";
import { absoluteUrl } from "@/lib/site";
import { ogImageMeta } from "@/lib/og-image";
import { PageHero } from "@/components/site/PageHero";
import { PanelSection } from "@/components/site/PanelSection";
import { PageCta } from "@/components/site/PageCta";
import { AccesosRapidos } from "@/components/site/AccesosRapidos";
import { waLink } from "@/lib/whatsapp";

const TITLE = "Blog de cuidado para adultos mayores | Oasis Cuernavaca";
const DESCRIPTION =
  "Guías sobre cuidado de adultos mayores, salud geriátrica, costos y cómo elegir una residencia en Cuernavaca. Escrito por el equipo de Residencia Oasis.";

type BlogSearch = { page?: number; categoria?: string };

// Parseo manual (sin zod) para no meter el validador en el bundle inicial:
// `validateSearch` no se code-splittea.
function searchSchema(input: Record<string, unknown>): BlogSearch {
  const out: BlogSearch = {};
  const page = Number(input["page"]);
  if (Number.isInteger(page) && page >= 1) out.page = page;
  const categoria = typeof input["categoria"] === "string" ? input["categoria"].trim() : "";
  if (categoria) out.categoria = categoria.slice(0, 100);
  return out;
}

export const Route = createFileRoute("/blog/")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ page: search.page ?? 1, categoria: search.categoria ?? null }),
  loader: ({ deps }) =>
    getBlogIndex({ data: { page: deps.page, category: deps.categoria } }),
  head: ({ loaderData }) => {
    const page = loaderData?.page ?? 1;
    const canonical = page > 1 ? `${absoluteUrl("/blog")}?page=${page}` : absoluteUrl("/blog");
    return ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: absoluteUrl("/blog") },
      ...ogImageMeta(),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbSchema([{ name: "Blog", path: "/blog" }]),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["CollectionPage", "Blog"],
          "@id": absoluteUrl("/blog"),
          url: absoluteUrl("/blog"),
          name: TITLE,
          description: DESCRIPTION,
          inLanguage: "es-MX",
          isPartOf: { "@id": WEBSITE_ID },
          publisher: { "@id": ORG_ID },
        }),
      },
    ],
  });
  },
  component: BlogIndex,
  errorComponent: BlogIndexError,
});

function BlogIndexError({ reset }: { reset: () => void }) {
  return (
    <section className="container-prose section-y">
      <h1 className="h-display">Blog</h1>
      <p className="answer-first">
        No pudimos cargar los artículos en este momento. Vuelva a intentarlo: suele resolverse al
        instante.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <button type="button" onClick={reset} className="btn-base btn-primary-cta">
          Volver a intentar
        </button>
        <Link to="/" className="btn-base btn-outline-cta">
          Ir al inicio
        </Link>
      </div>
    </section>
  );
}

function BlogIndex() {
  const { posts, page, totalPages, categories, category } = Route.useLoaderData();
  const waBlog = waLink("blog", "quiero información sobre Residencia Oasis");

  return (
    <>
      <PageHero
        breadcrumb="Blog"
        title="Blog: guías de cuidado para adultos mayores"
        answer="Escribimos para hijos e hijas que están decidiendo el cuidado de su mamá o su papá: costos reales, señales de alerta, qué preguntar al visitar una residencia y cómo es la vida diaria en Cuernavaca."
      />

      <PanelSection tone="soft" eyebrow="Artículos" label="Artículos del blog">
        {categories.length > 0 && (
          <nav aria-label="Categorías del blog" className="flex flex-wrap gap-2">
            <Link
              to="/blog"
              search={{ page: 1 }}
              className="chip-warm"
              aria-current={!category ? "page" : undefined}
              style={!category ? { borderColor: "var(--primary)" } : undefined}
            >
              Todos
            </Link>
            {categories.map((c: string) => (
              <Link
                key={c}
                to="/blog"
                search={{ page: 1, categoria: c }}
                className="chip-warm"
                aria-current={category === c ? "page" : undefined}
                style={category === c ? { borderColor: "var(--primary)" } : undefined}
              >
                {categoryLabel(c)}
              </Link>
            ))}
          </nav>
        )}

        {posts.length === 0 ? (
          <p className="answer-first mt-5">Pronto publicaremos artículos en esta sección.</p>
        ) : (
          <ul className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((post: BlogPostCard, i: number) => (
              <li key={post.slug} className="border border-border rounded-xl p-4 flex flex-col">
                {post.cover_image && (
                  <img
                    src={post.cover_image}
                    alt={post.title}
                    width={640}
                    height={360}
                    loading={i === 0 ? "eager" : "lazy"}
                    fetchPriority={i === 0 ? "high" : undefined}
                    decoding={i === 0 ? "sync" : "async"}
                    className="mb-4 aspect-[16/9] w-full rounded-xl object-cover"
                  />
                )}
                <p className="eyebrow">{categoryLabel(post.category)}</p>
                <h2 className="h-card mt-2">
                  <Link to="/blog/$slug" params={{ slug: post.slug }} className="hover:underline">
                    {post.title}
                  </Link>
                </h2>
                {post.excerpt && <p className="mt-3 text-muted-foreground">{post.excerpt}</p>}
                <p className="mt-4 text-sm text-muted-foreground">
                  <time dateTime={isoDate(post.published_at)}>{formatDateEs(post.published_at)}</time>
                  {post.reading_time > 0 && <> · {post.reading_time} min de lectura</>}
                </p>
              </li>
            ))}
          </ul>
        )}

        {totalPages > 1 && (
          <nav aria-label="Paginación" className="mt-10 flex items-center justify-center gap-3">
            {page > 1 && (
              <Link
                to="/blog"
                search={{ page: page - 1, ...(category ? { categoria: category } : {}) }}
                className="btn-base btn-outline-cta btn-compact"
              >
                Anterior
              </Link>
            )}
            <span className="text-muted-foreground">
              Página {page} de {totalPages}
            </span>
            {page < totalPages && (
              <Link
                to="/blog"
                search={{ page: page + 1, ...(category ? { categoria: category } : {}) }}
                className="btn-base btn-outline-cta btn-compact"
              >
                Siguiente
              </Link>
            )}
          </nav>
        )}
      </PanelSection>

      <AccesosRapidos variant="panel" exclude={["/blog"]} />

      <PageCta waHref={waBlog} />
    </>
  );
}
